import * as signalR from "@microsoft/signalr";
import { AVAuth } from "../Platform/AVAuth";
import * as AV from "./Interfaces/AV.Interfaces";
import { Server } from "../Platform/Server";

export class SignalRService {
  // The SignalR hub URL from azure
  private static hubUrl = "/SignalRHub";

  // Holds the SignalR connection instance
  public static connection: signalR.HubConnection;

  //Last time typing was sent, only send every 2 seconds
  private static lastTypingSent: number = 0;
  private static lastTypingRecieved: number = 0;

  // Track joined groups
  private static joinedLiveGroups = new Set<string>();
  private static joinedGroups = new Map<string, string | null>();

  private static stillNoFocus: number = 0;
  private static interval: any;
  private static typingDelay: number = 8000; // 8 seconds
  private static clearTypingDelay: number = 10000; // 10 seconds
  private static lostFocusDelay: number = 20000; // 20 seconds
  private static inactiveDelay: number = 60000; // 1 min
  private static lastActive: number = 0; // 30 seconds
  private static hubTimeout: number = 3000; // 3 seconds
  private static clearTypingInterval: any;
  private static clearTypingFunc: () => void;
  private static setTypingFunc: (
    receivedConvoId: string,
    userName: string,
  ) => void;
  private static preStartFunc: (conn: signalR.HubConnection) => void;

  private static _liveJoinCallback: any = null;
  private static _liveLeftCallback: any = null;
  private static _liveMessageCallback: any = null;
  private static _liveReactionCallback: any = null;
  private static _liveEndedCallback: any = null;

  //What to call when  clearing typing
  public static onClearTyping(onFunc: () => void) {
    SignalRService.clearTypingFunc = onFunc;
  }

  //What to call when receiving typing
  public static onTyping(
    onFunc: (receivedConvoId: string, userName: string) => void,
  ) {
    SignalRService.setTypingFunc = onFunc;
  }

  // What to call before starting the connection
  public static onPreStart(onFunc: (conn: signalR.HubConnection) => void) {
    var oldfunc = SignalRService.preStartFunc;
    SignalRService.preStartFunc = onFunc;

    if (SignalRService.connection && !oldfunc) {
      SignalRService.preStartFunc(SignalRService.connection);
    }
  }

  // Returns true if the SignalR connection is active
  public static isActive() {
    if (
      SignalRService.connection &&
      SignalRService.connection.state === signalR.HubConnectionState.Connected
    )
      return true;
    return false;
  }

  // Regoins all groups that the user was previously a member of
  private static rejoinAllGroups() {
    SignalRService.joinedGroups.forEach((type, convoId) =>
      SignalRService.connection
        ?.invoke("AddConvo", convoId, type)
        .catch((error) => {
          console.error("Error rejoining group:", error);
        }),
    );
  }

  // Before starting the SignalR service and initializes the connection
  private static preStart() {
    if (SignalRService.preStartFunc)
      SignalRService.preStartFunc(SignalRService.connection);

    SignalRService.connection.on(
      "typing",
      (receivedConvoId: string, userName: string) => {
        SignalRService.lastTypingRecieved = new Date().getTime();
        if (SignalRService.setTypingFunc) {
          SignalRService.setTypingFunc(receivedConvoId, userName);
        }
      },
    );
  }

  public static clearTyping() {
    SignalRService.lastTypingRecieved = 0;
    if (SignalRService.clearTypingFunc) {
      SignalRService.clearTypingFunc();
    }
  }

  // Initializes the SignalR service
  private static init() {
    if (!SignalRService.clearTypingInterval) {
      SignalRService.clearTypingInterval = setInterval(() => {
        if (
          SignalRService.lastTypingRecieved !== 0 &&
          SignalRService.lastTypingRecieved + SignalRService.clearTypingDelay <
            new Date().getTime()
        ) {
          SignalRService.clearTyping();
        }
      }, 500);
    }

    if (!SignalRService.interval) {
      SignalRService.interval = setInterval(() => {
        if (
          !(
            SignalRService.lastActive === 0 ||
            new Date().getTime() <=
              SignalRService.lastActive + SignalRService.inactiveDelay
          )
        ) {
          // If the user is not active stop the connection
          SignalRService.lastActive = 0; // Reset the lastactive flag
          console.log("User is inactives, stopping SignalR connection.");

          // DO NOT STOP if any active groups exist
          if (
            SignalRService.joinedLiveGroups.size > 0 ||
            SignalRService.joinedGroups.size > 0
          ) {
            return;
          }

          SignalRService.stopSignalRConnection().catch((error) => {
            console.error("Error stopping SignalR connection:", error);
          });
          return;
        }

        if (
          SignalRService.stillNoFocus === 0 ||
          new Date().getTime() <=
            SignalRService.stillNoFocus + SignalRService.lostFocusDelay
        )
          return;

        // If the user has not regained focus after 30 seconds, stop the connection
        SignalRService.stillNoFocus = 0; // Reset the stillNoFocus flag
        console.log(
          "User has lost focus for 30 seconds, stopping SignalR connection.",
        );

        // DO NOT STOP if any active groups exist
        if (
          SignalRService.joinedLiveGroups.size > 0 ||
          SignalRService.joinedGroups.size > 0
        ) {
          return;
        }

        SignalRService.stopSignalRConnection().catch((error) => {
          console.error("Error stopping SignalR connection:", error);
        });
      }, 1000);
    }
  }

  // When the user has focus, reset the stillNoFocus flag
  public static async gotFocus() {
    SignalRService.stillNoFocus = 0;
  }

  // When the user has lost focus, set the stillNoFocus flag to the current time
  public static async lostFocus() {
    // If the user has lost focus, set the stillNoFocus flag to true
    SignalRService.stillNoFocus = new Date().getTime();
  }

  // Creates the SignalR connection with the user's authentication token
  private static async createConnection() {
    // If the connection already exists, exit the function
    if (
      SignalRService.connection &&
      !(
        SignalRService.connection.state ===
          signalR.HubConnectionState.Disconnected ||
        SignalRService.connection.state ===
          signalR.HubConnectionState.Disconnecting
      )
    )
      return;

    try {
      // Factory function to get the authentication token
      const accessTokenFactory = async () => {
        const token = await AVAuth.getToken();
        if (!token) {
          throw new Error("Authentication token is not available");
        }

        return token;
      };

      var headers: string = "";
      if (Server.getImpersonate() != "") {
        headers = "?AVImpersonate=" + Server._impersonate;
      }
      if (Server.isNative()) {
        if (headers == "") headers = "?";
        else headers += "&";
        headers = "X-Actavivo=" + true;
      }

      // Build the SignalR connection
      SignalRService.connection = new signalR.HubConnectionBuilder()
        .withUrl(SignalRService.hubUrl + headers, {
          accessTokenFactory: accessTokenFactory, // Ensure token is included in each request
          //transport: signalR.HttpTransportType.WebSockets
          headers: { "AV-Client": "WebApp" },
        })

        .withAutomaticReconnect() // Optionally enable automatic reconnection
        .configureLogging(signalR.LogLevel.Information) // Enable information-level logging
        .build();

      SignalRService.connection.onreconnected(async () => {
        console.log("Reconnected");

        // Rejoin LIVE
        SignalRService.joinedLiveGroups.forEach(async (channelId) => {
          await SignalRService.connection.invoke("JoinLiveGroup", channelId);
        });

        // Rejoin CONVO
        SignalRService.rejoinAllGroups();

        // RESTORE LISTENERS
        if (SignalRService._liveMessageCallback) {
          SignalRService.connection.off("ReceiveLiveMessage");
          SignalRService.connection.on(
            "ReceiveLiveMessage",
            SignalRService._liveMessageCallback,
          );
        }

        if (SignalRService._liveReactionCallback) {
          SignalRService.connection.off("ReceiveLiveReaction");
          SignalRService.connection.on(
            "ReceiveLiveReaction",
            SignalRService._liveReactionCallback,
          );
        }

        if (SignalRService._liveEndedCallback) {
          SignalRService.connection.off("LiveEnded");
          SignalRService.connection.on(
            "LiveEnded",
            SignalRService._liveEndedCallback,
          );
        }

        if (this._liveJoinCallback) {
          SignalRService.connection.off("UserJoined");
          SignalRService.connection.on(
            "UserJoined",
            SignalRService._liveJoinCallback,
          );
        }

        if (this._liveLeftCallback) {
          SignalRService.connection.off("UserLeft");
          SignalRService.connection.on(
            "UserLeft",
            SignalRService._liveLeftCallback,
          );
        }
      });

      // Set KeepAliveInterval and ServerTimeout
      SignalRService.connection.keepAliveIntervalInMilliseconds = 10000; //10 Seconds
      SignalRService.connection.serverTimeoutInMilliseconds = 300000; //5 Minutes

      console.log(
        "Signal R connection Data: ",
        SignalRService.connection.baseUrl,
      );

      // Add the new connection to the list
      //SignalRService.connections.push(SignalRService.connection);
    } catch (error) {
      console.error("Error creating SignalR connection:", error);
      //throw error;
    }
  }

  private static async waitforConnectionOpen(): Promise<void> {
    var start = new Date().getTime();

    while (
      start + SignalRService.hubTimeout > new Date().getTime() &&
      (SignalRService.connection?.state ===
        signalR.HubConnectionState.Connecting ||
        SignalRService.connection?.state ===
          signalR.HubConnectionState.Reconnecting)
    ) {
      await new Promise((f) => setTimeout(f, 100));
    }
  }

  // Starts the SignalR connection
  private static async startSignalRConnection() {
    try {
      if (!SignalRService.connection) await SignalRService.createConnection();

      // Check if the connection is in the Disconnected state before starting
      if (
        SignalRService.connection?.state ===
        signalR.HubConnectionState.Disconnected
      ) {
        SignalRService.preStart();
        await SignalRService.connection.start();

        console.log("SignalR Connected");
      }

      await SignalRService.waitforConnectionOpen();

      if (
        SignalRService.connection?.state ===
        signalR.HubConnectionState.Connected
      )
        SignalRService.rejoinAllGroups();
      else console.log("SignalR Failed to connect");
    } catch (error) {
      console.error("SignalR Connection Error: ", error);
      //throw error;
    }
  }

  // Stops the SignalR connection
  public static async stopSignalRConnection() {
    try {
      if (SignalRService.connection) {
        await SignalRService.connection.stop();
        //@ts-ignore
        SignalRService.connection = null;
        console.log("SignalR Connection Stopped");
      }
    } catch (error) {
      console.error("Error stopping SignalR connection:", error);
      //throw error;
    }
  }

  // Checks the SignalR connection status and ensures it is connected
  private static async checkConnection(): Promise<boolean> {
    try {
      SignalRService.lastActive = new Date().getTime();
      SignalRService.init();
      // Ensure the SignalR connection is created and running
      if (!SignalRService.connection) {
        // Create a new SignalR connection if none exists
        await SignalRService.createConnection();
        await SignalRService.startSignalRConnection();
        await SignalRService.waitforConnectionOpen();
      }

      // Start the connection if it's in the disconnected state
      if (
        SignalRService.connection?.state !==
        signalR.HubConnectionState.Connected
      )
        await SignalRService.startSignalRConnection();

      SignalRService.gotFocus();
      await SignalRService.waitforConnectionOpen();

      if (
        SignalRService.connection?.state ===
        signalR.HubConnectionState.Connected
      )
        return true;

      return false;
    } catch (error) {
      console.error("Error check connection:", error);
      //throw error;
      return false;
    }
  }

  // Sends typing status to the server for a specific conversation
  public static async sendTyping(convoId: string, type: string | null = null) {
    //Only send every 2 sec
    if (
      SignalRService.lastTypingSent + SignalRService.typingDelay >
      new Date().getTime()
    )
      return;
    try {
      if (await SignalRService.checkConnection()) {
        await SignalRService.connection.invoke("SendTyping", convoId, type);
        SignalRService.lastTypingSent = new Date().getTime();
        console.log("SendTyping invoked successfully");
      } else {
        console.error("SignalR connection is not in the connected state.");
      }
    } catch (error) {
      console.error("Error invoking SendTyping:", error);
      // throw error;
    }
  }

  // Gets the current user's information
  public static async getSelf(): Promise<AV.IPerson> {
    try {
      if (await SignalRService.checkConnection()) {
        var p = await SignalRService.connection?.invoke("GetSelf");
        return p;
      } else {
        throw new Error();
      }
    } catch (error) {
      console.error("Error invoking GetSelf:", error);
      //@ts-ignore
      return null;
    }
  }

  // Invokes a method on the SignalR hub with a parameter and returns the result
  public static async invoke<T, P>(method: string, param: P): Promise<T> {
    try {
      if (await SignalRService.checkConnection()) {
        var retval = await SignalRService.connection?.invoke(method, param);
        return retval;
      }
      throw new Error("Error invoking: " + method);
    } catch (error) {
      console.error("Error invoking: " + method, error);
      throw error;
    }
  }

  // Invokes a method on the SignalR hub with a parameter and returns the result
  public static async invokeNoResult<P>(
    method: string,
    param: P,
  ): Promise<void> {
    try {
      if (await SignalRService.checkConnection()) {
        await SignalRService.connection?.invoke(method, param);
      }
      throw new Error("Error invoking: " + method);
    } catch (error) {
      console.error("Error invoking: " + method, error);
      //throw error;
    }
  }

  // Joins a hub group based on conversation ID
  public static async joinGroup(convoId: string, type: string | null = null) {
    try {
      if (await SignalRService.checkConnection()) {
        if (this.joinedGroups.has(convoId)) {
          console.log(`Already joined group: ${convoId}`);
          return;
        }

        await SignalRService.connection?.invoke("AddConvo", convoId, type);
        this.joinedGroups.set(convoId, type);
        console.log("Joined group successfully");
      } else {
        console.error(
          "Invalid conversation ID or SignalR connection is not in the connected state.",
        );
      }
    } catch (error) {
      console.error("Error joining group:", error);
      //throw error;
    }
  }

  // Leaves a hub group based on conversation ID
  public static async leaveGroup(convoId: string) {
    try {
      if (await SignalRService.checkConnection()) {
        await SignalRService.connection?.invoke("RemoveConvo", convoId);
        this.joinedGroups.delete(convoId);
        console.log("Left group successfully");
      } else {
        console.error("SignalR connection is not in the connected state.");
      }
    } catch (error) {
      console.error("Error leaving group:", error);
      //throw error;
    }
  }

  public static async start(): Promise<void> {
    await SignalRService.checkConnection();
  }

  // Optionally, export the connection object if needed externally
  //public static async getConnection(): Promise<signalR.HubConnection | null>
  //{
  //    if (!await SignalRService.checkConnection()) {
  //        console.error("Error getting connection");
  //        return null;
  //    }
  //    return SignalRService.connection;
  //}

  // JOIN LIVE CHAT GROUP
  public static async joinLiveChat(channelId: string) {
    try {
      if (!channelId) return;

      if (this.joinedLiveGroups.has(channelId)) {
        console.log("Already joined:", channelId);
        return;
      }

      if (await SignalRService.checkConnection()) {
        await SignalRService.connection.invoke("JoinLiveGroup", channelId);
        this.joinedLiveGroups.add(channelId);

        console.log("Joined live group successfully:", channelId);
      } else {
        console.error("SignalR not connected");
      }
    } catch (error) {
      console.error("Error joining live group:", error);
    }
  }

  // LEAVE LIVE CHAT GROUP
  public static async leaveLiveChat(channelId: string) {
    try {
      if (!channelId) return;

      if (!this.joinedLiveGroups.has(channelId)) return;

      if (await SignalRService.checkConnection()) {
        await SignalRService.connection.invoke("LeaveLiveGroup", channelId);
        this.joinedLiveGroups.delete(channelId);

        console.log("Left live group successfully:", channelId);
      } else {
        console.error("SignalR not connected");
      }
    } catch (error) {
      console.error("Error leaving live group:", error);
    }
  }

  // SEND LIVE MESSAGE
  public static async sendLiveMessage(channelId: string, message: any) {
    try {
      if (!channelId) return;

      if (await SignalRService.checkConnection()) {
        await SignalRService.connection.invoke("SendLiveMessage", {
          ChannelId: channelId,
          Message: message,
        });

        console.log("Live message sent successfully:", channelId);
      } else {
        console.error("SignalR not connected");
      }
    } catch (error) {
      console.error("Error sending live message:", error);
    }
  }

  // SEND LIVE REACTION
  public static async sendLiveReaction(channelId: string, emoji: string) {
    try {
      if (!channelId) return;

      if (await SignalRService.checkConnection()) {
        await SignalRService.connection.invoke(
          "SendLiveReaction",
          channelId,
          emoji,
        );

        console.log("Live reaction sent successfully:", emoji);
      } else {
        console.error("SignalR not connected");
      }
    } catch (error) {
      console.error("Error sending live reaction:", error);
    }
  }

  // SEND LIVE END
  public static async sendLiveEnd(channelId: string) {
    try {
      if (!channelId) return;

      if (await SignalRService.checkConnection()) {
        await SignalRService.connection.invoke("SendLiveEnd", channelId);

        console.log("LiveEnd sent:", channelId);
      }
    } catch (error) {
      console.error("Error sending LiveEnd:", error);
    }
  }

  // RECEIVE LIVE MESSAGE
  public static async onLiveMessage(callback: (msg: any) => void) {
    try {
      SignalRService._liveMessageCallback = callback;

      if (await SignalRService.checkConnection()) {
        // prevent duplicate listeners
        SignalRService.connection.off("ReceiveLiveMessage");

        SignalRService.connection.on("ReceiveLiveMessage", callback);

        console.log("Live message listener registered successfully");
      } else {
        console.error("SignalR not connected");
      }
    } catch (error) {
      console.error("Error setting live message listener:", error);
    }
  }

  // RECEIVE LIVE REACTION
  public static async onLiveReaction(callback: (data: any) => void) {
    try {
      SignalRService._liveReactionCallback = callback;

      if (await SignalRService.checkConnection()) {
        // prevent duplicate listeners
        SignalRService.connection.off("ReceiveLiveReaction");

        SignalRService.connection.on("ReceiveLiveReaction", callback);

        console.log("Live reaction listener registered successfully");
      } else {
        console.error("SignalR not connected");
      }
    } catch (error) {
      console.error("Error setting live reaction listener:", error);
    }
  }

  // RECEIVE LIVE ENDED
  public static async onLiveEnded(callback: (data: any) => void) {
    try {
      SignalRService._liveEndedCallback = callback;

      if (await SignalRService.checkConnection()) {
        // prevent duplicate listeners
        SignalRService.connection.off("LiveEnded");

        SignalRService.connection.on("LiveEnded", callback);

        console.log("LiveEnded listener registered");
      }
    } catch (error) {
      console.error("Error setting LiveEnded listener:", error);
    }
  }
}
